import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  useMemo & useCallback — Memoization
//  Chapter 4 · Hooks
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `import { useState, useMemo } from 'react';

function Analytics({ transactions }) {
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');

  // ✅ Expensive calculation — only recomputes when transactions changes
  const stats = useMemo(() => {
    console.log('Computing stats...'); // You'll see this only when transactions changes
    return {
      total:   transactions.reduce((s, t) => s + t.amount, 0),
      average: transactions.reduce((s, t) => s + t.amount, 0) / transactions.length,
      max:     Math.max(...transactions.map(t => t.amount)),
      count:   transactions.length,
    };
  }, [transactions]);

  // ✅ Filtered list — recomputes when filter, search, or transactions changes
  const filtered = useMemo(() => {
    return transactions
      .filter(t => filter === 'all' || t.type === filter)
      .filter(t => t.description.toLowerCase().includes(search.toLowerCase()));
  }, [transactions, filter, search]);

  return (
    <div>
      <StatsGrid stats={stats} />
      <TransactionList items={filtered} />
    </div>
  );
}`;

const CODE_2 = `import { useState, useCallback, memo } from 'react';

// Memoized child — only re-renders if its props change
const ExpensiveList = memo(function ExpensiveList({ items, onDelete, onEdit }) {
  console.log('ExpensiveList rendered');
  return (
    <ul>
      {items.map(item => (
        <li key={item.id}>
          {item.name}
          <button onClick={() => onDelete(item.id)}>Delete</button>
          <button onClick={() => onEdit(item)}>Edit</button>
        </li>
      ))}
    </ul>
  );
});

function ParentComponent() {
  const [items, setItems] = useState([...]);
  const [count, setCount] = useState(0);

  // ✅ useCallback — same reference if items doesn't change
  const handleDelete = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);    // No dependencies — never recreated

  const handleEdit = useCallback((item) => {
    openEditModal(item);
  }, []);

  return (
    <div>
      {/* Clicking this button won't re-render ExpensiveList
          because handleDelete and handleEdit are stable */}
      <button onClick={() => setCount(c => c + 1)}>
        Unrelated counter: {count}
      </button>

      <ExpensiveList
        items={items}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function UseMemoAndUseCallback() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 4 · Hooks</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>useMemo & useCallback — Memoization</h1>
        <p style={styles.desc}>Optimise performance by memoizing expensive calculations and function references.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Why Memoize? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Why Memoize?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Every time a component re-renders, all its functions and computed values are recreated. For expensive calculations or functions passed as props to memoized children, this causes unnecessary work. useMemo and useCallback cache results between renders and only recompute when specified dependencies change." }} />
      </div>

      /* ── useMemo — Memoize Values ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>useMemo — Memoize Values</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "useMemo caches the result of an expensive calculation. Only recomputes when its dependencies change." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── useCallback — Memoize Functions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>useCallback — Memoize Functions</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "useCallback returns the same function reference between renders. Essential when passing callbacks to memoized child components (React.memo)." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the difference between useMemo and useCallback?</div>
          <div style={styles.qItem}>✦ 2. Should you add useMemo/useCallback everywhere? Why not?</div>
          <div style={styles.qItem}>✦ 3. What does React.memo do and how does it work with useCallback?</div>
          <div style={styles.qItem}>✦ 4. When does useMemo not help performance?</div>
          <div style={styles.qItem}>✦ 5. Implement a filtered, sorted product list using useMemo with multiple filter states.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
